import React, { useRef, useState } from "react";
import { UploadCloud, FileText, AlertCircle, Trash2, X, CheckCircle2, CreditCard } from "lucide-react";
import { cn } from "../../../lib/cn";

/**
 * FileDrop — ui-v2.5. Subida de archivos. Especificación: catálogo · sección FileDrop.
 * Variante `wide` (documentos) y `square` (imágenes, p. ej. DNI). Estados: vacío, con archivo, error.
 * VALIDACIÓN DE TAMAÑO EN CLIENTE antes de subir (maxSize en MB): si excede, no llama a onChange
 * y muestra el error con el nombre y tamaño del archivo rechazado.
 *
 * <FileDrop variant="wide" accept="application/pdf,image/*" maxSize={10}
 *           value={file} onChange={setFile} hint="PDF, JPG o PNG · máx. 10 MB" />
 *  - value: File | { name, size } | null.  onChange: (file|null) => void.
 *  - error: string externo opcional (además de la validación interna de tamaño).
 */

function humanSize(bytes) {
  if (bytes == null) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function FileDrop({ variant = "wide", accept, maxSize, value, onChange, error: errorProp, hint, className }) {
  const inputRef = useRef(null);
  const [internalErr, setInternalErr] = useState(null);
  const [rejected, setRejected] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const error = errorProp || internalErr;

  const pick = (file) => {
    if (!file) return;
    if (maxSize && file.size > maxSize * 1024 * 1024) {
      setRejected({ name: file.name, size: file.size });
      setInternalErr(`El archivo supera ${maxSize} MB`);
      return;
    }
    setInternalErr(null);
    setRejected(null);
    onChange?.(file);
  };
  const openPicker = () => inputRef.current?.click();
  const onInput = (e) => pick(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    pick(e.dataTransfer.files?.[0]);
  };
  const clear = (e) => {
    e?.stopPropagation();
    setInternalErr(null);
    setRejected(null);
    onChange?.(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const hasFile = !!value && !error;
  const errFile = rejected || (errorProp && value ? value : null);

  const hiddenInput = (
    <input ref={inputRef} type="file" accept={accept} onChange={onInput} className="hidden" />
  );

  // ---------- CUADRADA ----------
  if (variant === "square") {
    if (error) {
      return (
        <div className={className}>
          {hiddenInput}
          <div className="grid h-[95px] w-[140px] place-items-center gap-1 rounded-v25-md border border-red-600 bg-red-50 px-2 text-center" style={{ borderStyle: "solid" }}>
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="text-[11px] font-medium text-red-800">Demasiado grande</span>
          </div>
        </div>
      );
    }
    return (
      <div className={className}>
        {hiddenInput}
        {hasFile ? (
          <div className="grid h-[95px] w-[140px] place-items-center gap-1 rounded-v25-md border border-v25-brand-600 bg-v25-prefill-bg px-2 text-center" style={{ borderStyle: "solid" }}>
            <CheckCircle2 className="h-5 w-5 text-v25-ok" />
            <span className="text-[11px] font-medium text-v25-active-fg">Subido</span>
            <span className="truncate text-[10px] text-v25-ink-400" style={{ maxWidth: 120 }}>{value.name}</span>
          </div>
        ) : (
          <button
            type="button"
            onClick={openPicker}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={cn(
              "grid h-[95px] w-[140px] cursor-pointer place-items-center gap-1.5 rounded-v25-md border-[1.5px] border-dashed border-v25-ink-400 bg-white px-2 text-center transition-colors hover:border-v25-brand-600 hover:bg-[#f0f7ff] focus-visible:outline-none focus-visible:shadow-v25-focus",
              dragOver && "border-v25-brand-600 bg-[#f0f7ff]"
            )}
          >
            <CreditCard className="h-6 w-6 text-v25-ink-400" />
            <span className="text-[11px] text-v25-ink-400">Toca para subir</span>
          </button>
        )}
      </div>
    );
  }

  // ---------- ANCHA ----------
  if (error) {
    return (
      <div className={className}>
        {hiddenInput}
        <div className="flex items-center gap-3 rounded-v25-md border border-red-600 bg-red-50 p-3" style={{ borderStyle: "solid" }}>
          <AlertCircle className="h-5 w-5 shrink-0 text-red-600" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-red-800">{error}</p>
            {errFile && <p className="text-[11px] text-red-600">{errFile.name} · {humanSize(errFile.size)}</p>}
          </div>
          <button type="button" onClick={clear} aria-label="Descartar" className="grid place-items-center p-1.5 text-red-600 hover:opacity-80">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {hiddenInput}
      {hasFile ? (
        <div className="flex items-center gap-3 rounded-v25-md border border-v25-brand-600 bg-v25-prefill-bg p-3" style={{ borderStyle: "solid" }}>
          <FileText className="h-5 w-5 shrink-0 text-v25-brand-600" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-v25-ink-700">{value.name}</p>
            <p className="text-[11px] text-v25-ink-400">{humanSize(value.size)} · subido</p>
          </div>
          <button type="button" onClick={clear} aria-label="Eliminar archivo" className="grid place-items-center p-1.5 text-v25-ink-400 hover:text-red-500">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={cn(
            "flex w-full items-center gap-3 rounded-v25-md border-[1.5px] border-dashed border-v25-ink-400 bg-white p-3 text-left transition-colors hover:border-v25-brand-600 hover:bg-[#f0f7ff] focus-visible:outline-none focus-visible:shadow-v25-focus",
            dragOver && "border-v25-brand-600 bg-[#f0f7ff]"
          )}
        >
          <UploadCloud className="h-5 w-5 shrink-0 text-v25-ink-400" />
          <div className="min-w-0">
            <p className="text-sm text-v25-ink-500">Arrastra o selecciona un archivo</p>
            {hint && <p className="text-[11px] text-v25-ink-400">{hint}</p>}
          </div>
        </button>
      )}
    </div>
  );
}
